/**
 * A request that never reached the server looks different per transport: Angular's
 * HttpClient reports it as "Http failure response for <url>: 0 Unknown Error", signalR as
 * "WebSocket failed to connect". Both mean the same thing to the user - the server is
 * unreachable - so the markers live in one place: showError uses them to pick the soft
 * "service not available" notice over the application-error popup, and the global error
 * handler uses them to keep the raw transport noise out of Sentry (#336).
 */
const CONNECTIVITY_MARKERS = ['WebSocket failed to connect', 'Unknown Error'];

export function isConnectivityFailure(message: unknown): boolean {
    const text = typeof message === 'string' ? message : String(message || '');

    return CONNECTIVITY_MARKERS.some(marker => text.indexOf(marker) !== -1);
}

/**
 * What a gateway answers for an application server that is down or too slow: to the user that is
 * the server being unavailable, the same as a request that never reached it.
 */
const GATEWAY_STATUSES = [502, 503, 504];

/**
 * The HTTP status the application server itself answered a failed request with, or 0 when it gave
 * no answer of its own. Only such an answer has a text worth showing (#336).
 */
export function serverAnswerStatus(error: unknown): number {
    const status = (error && (error as { status?: number }).status) || 0;

    return GATEWAY_STATUSES.indexOf(status) === -1 ? status : 0;
}

/** An HTTP request that failed without an answer from the application server. A script error has no status. */
export function isServerUnreachable(error: unknown): boolean {
    const status = error && (error as { status?: number }).status;

    return typeof status === 'number' && serverAnswerStatus(error) === 0;
}
